import * as Yup from 'yup';
import {validateCPF, validateEmail} from '../../../../utils/index.js';
import {
    dataBrValida,
    ehMenorDeIdade,
    exibirResponsavelLegal,
    exigeDadosResponsavel,
    regrasDoPrograma
} from './inscricaoAdministrativaPayload.js';

/*
 * Validação do formulário de inscrição do administrativo.
 * As regras dependem de outros campos e do processo escolhido (contexto do
 * useForm), por isso ficam numa lista de regras sobre o formulário inteiro,
 * cada uma com o campo onde o erro aparece e a seção desse campo — a etapa e
 * a seção que abrem quando o Salvar encontra erro fora da tela atual.
 *
 * Contexto: {programaId, exigirCategoria, temResponsavelCadastrado, hoje}.
 */

/* Etapas do Stepper e, em cada uma, as seções (abas verticais) na ordem da tela. */
export const ETAPAS = Object.freeze([
    {id: 'pessoal', label: 'Dados Pessoais', secoes: ['dadosPessoais', 'enderecoResidencial', 'responsavelLegal']},
    {id: 'socioeconomico', label: 'Socioeconômico', secoes: ['declaracaoMoradia', 'rendas', 'grupoFamiliar', 'parentes']},
    {id: 'inscricao', label: 'Dados da Inscrição', secoes: ['opcoes', 'processoCategoria', 'veterano', 'escolaridade', 'enem', 'motivacao']},
    {id: 'documentacao', label: 'Documentos', secoes: ['documentos']},
    {id: 'calendario', label: 'Calendário', secoes: ['calendario']},
    {id: 'avaliacao', label: 'Análise', secoes: ['analise', 'atendente']}
]);

/* Seção → índice da etapa. */
export const ETAPA_DA_SECAO = Object.freeze(ETAPAS.reduce((mapa, etapa, indice) => {
    etapa.secoes.forEach(secao => {
        mapa[secao] = indice;
    });
    return mapa;
}, {}));

const OBRIGATORIO = 'É obrigatório';
const OBRIGATORIA = 'É obrigatória';
const DATA_INVALIDA = 'Data inválida';

const vazio = valor => valor === null || valor === undefined || String(valor).trim() === '';
const digitos = valor => String(valor ?? '').replace(/\D/g, '');
const cpfInvalido = valor => !vazio(valor) && (digitos(valor).length !== 11 || !validateCPF(digitos(valor)));
const emailInvalido = valor => !vazio(valor) && !validateEmail(String(valor).trim());
const dataInvalida = valor => !vazio(valor) && !dataBrValida(valor);
const cepInvalido = valor => !vazio(valor) && digitos(valor).length !== 8;
const telefoneInvalido = (valor, tamanho) => !vazio(digitos(valor)) && digitos(valor).length !== tamanho;
export const PONTUACAO_ENEM = /^\d{1,3}(,\d{1,4})?$/;

const regras = ctx => regrasDoPrograma(ctx.programaId, {exigirCategoria: ctx.exigirCategoria});
const secaoResponsavel = (v, ctx) => exibirResponsavelLegal(v.pessoa?.dataNascimento, ctx.temResponsavelCadastrado, ctx.hoje);
const comResponsavel = (v, ctx) => secaoResponsavel(v, ctx) && exigeDadosResponsavel(v.tipoResponsavel);
const usaEnem = (v, ctx) => regras(ctx).exibirEnem && v.utilizarEnem === 'true';
const algumTelefone = v => [v.pessoa?.telefone, v.pessoa?.celular, v.pessoa?.celularDois].some(valor => !vazio(digitos(valor)));

/* Cada regra: campo do erro, seção, mensagem e quando o valor é inválido. Por campo, as condições são exclusivas. */
export const REGRAS = Object.freeze([
    {caminho: 'idProcessoSeletivo', secao: null, mensagem: OBRIGATORIO, invalido: v => vazio(v.idProcessoSeletivo)},
    {caminho: 'codigoAluno', secao: null, mensagem: OBRIGATORIO, invalido: (v, ctx) => regras(ctx).exibirCodigoAluno && vazio(v.codigoAluno)},
    {caminho: 'codigoAluno', secao: null, mensagem: 'Código inválido', invalido: (v, ctx) => regras(ctx).exibirCodigoAluno && !vazio(v.codigoAluno) && !/^\d{1,6}$/.test(String(v.codigoAluno).trim())},

    {caminho: 'pessoa.cpf', secao: 'dadosPessoais', mensagem: OBRIGATORIO, invalido: v => vazio(v.pessoa?.cpf)},
    {caminho: 'pessoa.cpf', secao: 'dadosPessoais', mensagem: 'CPF inválido', invalido: v => cpfInvalido(v.pessoa?.cpf)},
    {caminho: 'pessoa.nome', secao: 'dadosPessoais', mensagem: OBRIGATORIO, invalido: v => vazio(v.pessoa?.nome)},
    {caminho: 'pessoa.nomeSocial', secao: 'dadosPessoais', mensagem: OBRIGATORIO, invalido: v => !!v.pessoa?.habilitarNomeSocial && vazio(v.pessoa?.nomeSocial)},
    {caminho: 'pessoa.dataNascimento', secao: 'dadosPessoais', mensagem: OBRIGATORIA, invalido: v => vazio(v.pessoa?.dataNascimento)},
    {caminho: 'pessoa.dataNascimento', secao: 'dadosPessoais', mensagem: DATA_INVALIDA, invalido: v => dataInvalida(v.pessoa?.dataNascimento)},
    {caminho: 'tipoResponsavel', secao: 'responsavelLegal', mensagem: OBRIGATORIO, invalido: (v, ctx) => secaoResponsavel(v, ctx) && vazio(v.tipoResponsavel)},
    {
        caminho: 'tipoResponsavel', secao: 'responsavelLegal', mensagem: 'Menor de idade precisa de responsável ou ser emancipado',
        invalido: (v, ctx) => secaoResponsavel(v, ctx) && v.tipoResponsavel === 'NAO_TEM' && ehMenorDeIdade(v.pessoa?.dataNascimento, ctx.hoje)
    },
    {
        caminho: 'pessoa.dataEmancipacao', secao: 'responsavelLegal', mensagem: OBRIGATORIA,
        invalido: (v, ctx) => secaoResponsavel(v, ctx) && v.tipoResponsavel === 'EMANCIPADO' && vazio(v.pessoa?.dataEmancipacao)
    },
    {
        caminho: 'pessoa.dataEmancipacao', secao: 'responsavelLegal', mensagem: DATA_INVALIDA,
        invalido: (v, ctx) => secaoResponsavel(v, ctx) && v.tipoResponsavel === 'EMANCIPADO' && dataInvalida(v.pessoa?.dataEmancipacao)
    },
    {caminho: 'responsavel.nome', secao: 'responsavelLegal', mensagem: OBRIGATORIO, invalido: (v, ctx) => comResponsavel(v, ctx) && vazio(v.responsavel?.nome)},
    {caminho: 'responsavel.cpf', secao: 'responsavelLegal', mensagem: OBRIGATORIO, invalido: (v, ctx) => comResponsavel(v, ctx) && vazio(v.responsavel?.cpf)},
    {caminho: 'responsavel.cpf', secao: 'responsavelLegal', mensagem: 'CPF inválido', invalido: (v, ctx) => comResponsavel(v, ctx) && cpfInvalido(v.responsavel?.cpf)},
    {caminho: 'responsavel.rg', secao: 'responsavelLegal', mensagem: OBRIGATORIO, invalido: (v, ctx) => comResponsavel(v, ctx) && vazio(v.responsavel?.rg)},
    {caminho: 'responsavel.dataNascimento', secao: 'responsavelLegal', mensagem: OBRIGATORIA, invalido: (v, ctx) => comResponsavel(v, ctx) && vazio(v.responsavel?.dataNascimento)},
    {caminho: 'responsavel.dataNascimento', secao: 'responsavelLegal', mensagem: DATA_INVALIDA, invalido: (v, ctx) => comResponsavel(v, ctx) && dataInvalida(v.responsavel?.dataNascimento)},

    {caminho: 'endereco.cep', secao: 'enderecoResidencial', mensagem: OBRIGATORIO, invalido: v => vazio(digitos(v.endereco?.cep))},
    {caminho: 'endereco.cep', secao: 'enderecoResidencial', mensagem: 'CEP inválido', invalido: v => !vazio(digitos(v.endereco?.cep)) && cepInvalido(digitos(v.endereco?.cep))},
    {caminho: 'endereco.cidade', secao: 'enderecoResidencial', mensagem: OBRIGATORIA, invalido: v => !v.endereco?.cidade?.id},
    {caminho: 'pessoa.telefone', secao: 'dadosPessoais', mensagem: 'Informe ao menos um telefone', invalido: v => !algumTelefone(v)},
    {caminho: 'pessoa.telefone', secao: 'dadosPessoais', mensagem: 'Telefone inválido', invalido: v => telefoneInvalido(v.pessoa?.telefone, 10)},
    {caminho: 'pessoa.celular', secao: 'dadosPessoais', mensagem: 'Celular inválido', invalido: v => telefoneInvalido(v.pessoa?.celular, 11)},
    {caminho: 'pessoa.celularDois', secao: 'dadosPessoais', mensagem: 'Celular inválido', invalido: v => telefoneInvalido(v.pessoa?.celularDois, 11)},
    {caminho: 'pessoa.emailAtual', secao: 'dadosPessoais', mensagem: OBRIGATORIO, invalido: v => vazio(v.pessoa?.emailAtual)},
    {caminho: 'pessoa.emailAtual', secao: 'dadosPessoais', mensagem: 'E-mail inválido', invalido: v => emailInvalido(v.pessoa?.emailAtual)},
    {caminho: 'responsavel.endereco.logradouro', secao: 'responsavelLegal', mensagem: OBRIGATORIO, invalido: (v, ctx) => comResponsavel(v, ctx) && vazio(v.responsavel?.endereco?.logradouro)},
    {caminho: 'responsavel.endereco.numero', secao: 'responsavelLegal', mensagem: OBRIGATORIO, invalido: (v, ctx) => comResponsavel(v, ctx) && vazio(v.responsavel?.endereco?.numero)},
    {caminho: 'responsavel.endereco.bairro', secao: 'responsavelLegal', mensagem: OBRIGATORIO, invalido: (v, ctx) => comResponsavel(v, ctx) && vazio(v.responsavel?.endereco?.bairro)},
    {caminho: 'responsavel.endereco.cep', secao: 'responsavelLegal', mensagem: OBRIGATORIO, invalido: (v, ctx) => comResponsavel(v, ctx) && vazio(digitos(v.responsavel?.endereco?.cep))},
    {
        caminho: 'responsavel.endereco.cep', secao: 'responsavelLegal', mensagem: 'CEP inválido',
        invalido: (v, ctx) => comResponsavel(v, ctx) && !vazio(digitos(v.responsavel?.endereco?.cep)) && cepInvalido(digitos(v.responsavel?.endereco?.cep))
    },
    {caminho: 'responsavel.endereco.cidade', secao: 'responsavelLegal', mensagem: OBRIGATORIA, invalido: (v, ctx) => comResponsavel(v, ctx) && !v.responsavel?.endereco?.cidade?.id},
    {caminho: 'responsavel.celular', secao: 'responsavelLegal', mensagem: 'Celular inválido', invalido: (v, ctx) => comResponsavel(v, ctx) && telefoneInvalido(v.responsavel?.celular, 11)},
    {caminho: 'responsavel.emailAtual', secao: 'responsavelLegal', mensagem: OBRIGATORIO, invalido: (v, ctx) => comResponsavel(v, ctx) && vazio(v.responsavel?.emailAtual)},
    {caminho: 'responsavel.emailAtual', secao: 'responsavelLegal', mensagem: 'E-mail inválido', invalido: (v, ctx) => comResponsavel(v, ctx) && emailInvalido(v.responsavel?.emailAtual)},

    {caminho: 'cadUnicoOuTransferenciaRenda', secao: 'declaracaoMoradia', mensagem: OBRIGATORIO, invalido: (v, ctx) => regras(ctx).exibirCadUnico && vazio(v.cadUnicoOuTransferenciaRenda)},

    {caminho: 'vaga', secao: 'opcoes', mensagem: OBRIGATORIA, invalido: (v, ctx) => regras(ctx).exibirVaga && vazio(v.vaga)},
    {caminho: 'categoria', secao: 'processoCategoria', mensagem: OBRIGATORIA, invalido: (v, ctx) => regras(ctx).exibirCategoria && vazio(v.categoria)},
    {caminho: 'inscricaoEnem.cdEnem', secao: 'enem', mensagem: 'Código inválido', invalido: (v, ctx) => usaEnem(v, ctx) && !vazio(v.inscricaoEnem?.cdEnem) && !/^\d+$/.test(String(v.inscricaoEnem.cdEnem).trim())},
    {caminho: 'inscricaoEnem.pontuacao', secao: 'enem', mensagem: 'Pontuação inválida', invalido: (v, ctx) => usaEnem(v, ctx) && !vazio(v.inscricaoEnem?.pontuacao) && !PONTUACAO_ENEM.test(String(v.inscricaoEnem.pontuacao).trim())},
    {caminho: 'inscricaoVeterano.descricaoBeneficio', secao: 'veterano', mensagem: 'Máximo de 255 caracteres', invalido: (v, ctx) => regras(ctx).exibirVeterano && String(v.inscricaoVeterano?.descricaoBeneficio || '').length > 255},
    {caminho: 'inscricaoVeterano.descricaoProcessoAnterior', secao: 'veterano', mensagem: 'Máximo de 255 caracteres', invalido: (v, ctx) => regras(ctx).exibirVeterano && String(v.inscricaoVeterano?.descricaoProcessoAnterior || '').length > 255},

    {
        caminho: 'percentualBolsa', secao: 'analise', mensagem: 'Deve ser um número inteiro entre 0 e 100',
        invalido: v => {
            if (vazio(v.percentualBolsa)) return false;
            const numero = Number(String(v.percentualBolsa).replace(',', '.'));
            return !Number.isInteger(numero) || numero < 0 || numero > 100;
        }
    }
]);

/* Erros do formulário como [{caminho, mensagem}], na ordem das regras e só o primeiro de cada campo. */
export const validarInscricao = (valores, contexto = {}) => {
    const erros = [];
    const vistos = new Set();
    REGRAS.forEach(regra => {
        if (!vistos.has(regra.caminho) && regra.invalido(valores || {}, contexto)) {
            vistos.add(regra.caminho);
            erros.push({caminho: regra.caminho, mensagem: regra.mensagem});
        }
    });
    return erros;
};

/* Schema Yup (para o yupResolver): uma validação por regra, lendo o contexto passado ao resolver. */
export const criarSchemaInscricao = () => REGRAS.reduce((schema, regra, indice) => schema.test(
    `regra-${indice}`,
    regra.mensagem,
    function validar(valores) {
        const contexto = (this.options && this.options.context) || {};
        return regra.invalido(valores || {}, contexto) ? this.createError({path: regra.caminho, message: regra.mensagem}) : true;
    }
), Yup.object());

const SECAO_POR_CAMINHO = REGRAS.reduce((mapa, regra) => ({...mapa, [regra.caminho]: regra.secao}), {});

/* Caminhos ('pessoa.cpf', 'responsavel.endereco.cep'...) dos erros do react-hook-form. */
export const caminhosComErro = (erros, prefixo = '') => {
    if (!erros || typeof erros !== 'object') return [];
    if (typeof erros.message === 'string' && erros.type) return prefixo ? [prefixo] : [];
    return Object.keys(erros).reduce((lista, chave) => {
        if (chave === 'ref') return lista;
        return lista.concat(caminhosComErro(erros[chave], prefixo ? `${prefixo}.${chave}` : chave));
    }, []);
};

/* Seções (ids das ETAPAS) que têm erro. */
export const secoesComErro = erros => new Set(caminhosComErro(erros).map(caminho => SECAO_POR_CAMINHO[caminho]).filter(Boolean));

/* Índices das etapas que têm erro. */
export const etapasComErro = erros => new Set([...secoesComErro(erros)].map(secao => ETAPA_DA_SECAO[secao]));

/* Primeira seção com erro, na ordem das etapas e das seções: {etapa (índice), secao (id)} ou null. */
export const primeiraSecaoComErro = erros => {
    const secoes = secoesComErro(erros);
    for (let etapa = 0; etapa < ETAPAS.length; etapa++) {
        const secao = ETAPAS[etapa].secoes.find(id => secoes.has(id));
        if (secao) return {etapa, secao};
    }
    return null;
};

/* Índice de uma seção entre as visíveis (o GenericTab só conta as que têm visible !== false), ou -1. */
export const indiceSecaoVisivel = (conteudo, secao) =>
    (conteudo || []).filter(item => item && item.visible !== false).findIndex(item => item.id === secao);
